import { NoticeReceiver, NoticeTemplateIds } from '../models/NoticeReceiver';
import React, { useEffect, useState } from 'react';

import AuthService from '../../shared/services/AuthService';
import { Box } from '@mui/material';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import { CustomLoadingProgress } from '../../shared/components/CustomLoadingProgress';
import ErrorPanel from '../../shared/components/ErrorPanel';
import Grid from '@mui/material/Unstable_Grid2';
import IconButton from '@mui/material/IconButton/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { SUPPORT_MESSAGE } from '../../shared/constants';
import dayjs from 'dayjs';
import styles from '../styles/NotificationPanel.scss';
import { useLesson } from '../../dashboard/hooks/useLesson';
import { useNavigate } from 'react-router-dom';
import useNotifications from '../hooks/useNotifications';
import { useTheme } from '@mui/material/styles';

interface NotificationPanelProps {
  notification: NoticeReceiver;
}

export function NotificationPanel(props: NotificationPanelProps) {
  const navigate = useNavigate();
  const muiTheme = useTheme();
  const primaryColor = muiTheme.palette.primary.main;
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [showUpdateError, setShowUpdateError] = useState(false);
  const notificationAsset = props.notification.assets;
  const templatekey = props.notification.templatekey;

  const { updating, updateError, updateNotificationState } = useNotifications(
    AuthService.Userkey,
    false /* loading data not required here */
  );

  const { getModule, loadingModule, moduleData } = useLesson();

  useEffect(() => {
    if (updating) {
      setShowUpdateError(false);
    }
    if (updateError && !showUpdateError) {
      setShowUpdateError(true);
    }
  }, [updating, updateError]);

  /** Get coursekey from moduleData and navigate to relevant portfolio */
  useEffect(() => {
    if (moduleData) {
      handleNavigate(
        `/dashboard/course/${moduleData.coursekey}/${notificationAsset.modulekey}?layer=${notificationAsset.layer}`
      );
    }
  }, [moduleData]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  function handleSelect(action: NotificationAction) {
    handleClose();
    const { noticekey, receiverkey } = props.notification;
    updateNotificationState(
      noticekey,
      receiverkey,
      action === 'mark-as-read',
      action === 'dismiss'
    );
  }

  function handleClose() {
    setAnchorEl(null);
  }

  /** Get coursekey using modulekey */
  const handleAssessmentLink = () => {
    if (notificationAsset.modulekey) {
      getModule(notificationAsset.modulekey);
    }
  };

  const handleNavigate = (path: string) => {
    if (!props.notification.read) {
      handleSelect('mark-as-read');
    }
    navigate(path);
  };

  const handleNotificationClick = () => {
    switch (templatekey) {
      case 'CourseVersionPublish':
      case 'GroupCourseAdd':
        handleNavigate(`/dashboard/course/${notificationAsset.coursekey}`);
        break;
      case 'GroupMemberAdd':
        handleNavigate(`/dashboard`);
        break;
      case 'UserPortfolioLock':
      case 'UserPortfolioRetry':
        handleAssessmentLink();
        break;
      case 'UserPortfolioSubmit':
      case 'UserPortfolioResubmit':
        handleNavigate(`/assessments`);
        break;
      default:
        break;
    }
  };

  const noticeAccessible: boolean =
    templatekey && NoticeTemplateIds.includes(templatekey);

  return (
    <Box m={2}>
      <Grid container>
        <Grid>
          <div
            className={styles.notificationIndicator}
            style={{
              backgroundColor: props.notification.read
                ? 'transparent'
                : primaryColor
            }}
          ></div>
        </Grid>
        <Grid className={styles.notificationBody}>
          {loadingModule && <CustomLoadingProgress />}
          <Card
            onClick={noticeAccessible ? handleNotificationClick : undefined}
            style={{
              cursor: noticeAccessible ? 'pointer' : 'default'
            }}
          >
            {updating && <CustomLoadingProgress />}
            <CardHeader
              title={props.notification.subject}
              subheader={dayjs(props.notification.received).format(
                'DD MMM YYYY HH:mm'
              )}
              action={
                <IconButton aria-label="settings" onClick={handleClick}>
                  <MoreVertIcon />
                </IconButton>
              }
            />
            <CardContent>
              <div
                dangerouslySetInnerHTML={{
                  __html: props.notification.body
                }}
              />
              {showUpdateError && (
                <ErrorPanel
                  title="Failed to update your notifications"
                  message={SUPPORT_MESSAGE}
                />
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <NotificationMenu
        markedAsRead={props.notification.read}
        anchorEl={anchorEl}
        onSelect={handleSelect}
        onClose={handleClose}
      />
    </Box>
  );
}

type NotificationAction = 'mark-as-read' | 'dismiss';

interface NotificationMenuProps {
  markedAsRead: boolean;
  anchorEl: HTMLElement | null;
  onSelect: (action: NotificationAction) => void;
  onClose: () => void;
}

function NotificationMenu(props: NotificationMenuProps) {
  return (
    <Menu
      anchorEl={props.anchorEl}
      keepMounted
      open={Boolean(props.anchorEl)}
      onClose={props.onClose}
    >
      {!props.markedAsRead && (
        <MenuItem onClick={() => props.onSelect('mark-as-read')}>
          Mark as read
        </MenuItem>
      )}
      <MenuItem onClick={() => props.onSelect('dismiss')}>
        Dismiss notification
      </MenuItem>
    </Menu>
  );
}
